/**
 * Board Presentation Generator Types
 * Generate executive-ready presentations from metrics and data
 */

export type PresentationType =
  | 'monthly_update'
  | 'quarterly_review'
  | 'budget_request'
  | 'strategy_review'
  | 'incident_report';

export type SlideType =
  | 'title'
  | 'executive_summary'
  | 'kpis'
  | 'risks_opportunities'
  | 'investment_request'
  | 'roadmap'
  | 'team_update'
  | 'custom';

export interface KPIData {
  name: string;
  value: string | number;
  trend: 'up' | 'down' | 'stable';
  target?: string | number;
  unit?: string;
  changePercent?: number;
}

export interface RiskOpportunity {
  id: string;
  type: 'risk' | 'opportunity';
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  likelihood: 'high' | 'medium' | 'low';
  mitigation?: string;
}

export interface InvestmentItem {
  id: string;
  title: string;
  amount: number;
  currency: string;
  category: 'infrastructure' | 'tooling' | 'headcount' | 'training' | 'other';
  justification: string;
  roi?: string;
  timeline?: string;
}

export interface RoadmapItem {
  id: string;
  title: string;
  description?: string;
  quarter: string;
  status: 'completed' | 'in_progress' | 'planned' | 'at_risk';
  team?: string;
}

export interface SlideContent {
  // Title slide
  title?: string;
  subtitle?: string;
  date?: string;

  // Executive summary
  highlights?: string[];
  concerns?: string[];

  // KPIs
  kpis?: KPIData[];

  // Risks & Opportunities
  items?: RiskOpportunity[];

  // Investment
  investments?: InvestmentItem[];
  totalBudget?: number;

  // Roadmap
  roadmapItems?: RoadmapItem[];

  // Custom
  customMarkdown?: string;
}

export interface PresentationSlide {
  id: string;
  slide_type: SlideType;
  title: string;
  content: SlideContent;
  order: number;
  notes?: string;
}

export interface PresentationSettings {
  companyName?: string;
  presentationPeriod?: string;
  audienceType?: 'board' | 'executives' | 'investors' | 'all_hands';
  includeAppendix?: boolean;
}

export interface BoardPresentation {
  id: string;
  name: string;
  presentation_type: PresentationType;
  slides: PresentationSlide[];
  settings: PresentationSettings;
  created_at: string;
  updated_at: string;
}

// Presentation type configurations
export const PRESENTATION_TYPE_CONFIG: Record<PresentationType, {
  label: string;
  description: string;
  defaultSlides: SlideType[];
}> = {
  monthly_update: {
    label: 'Monthly Update',
    description: 'Regular monthly status update for the board',
    defaultSlides: ['title', 'executive_summary', 'kpis', 'risks_opportunities', 'roadmap'],
  },
  quarterly_review: {
    label: 'Quarterly Review',
    description: 'Comprehensive quarterly business review',
    defaultSlides: ['title', 'executive_summary', 'kpis', 'risks_opportunities', 'team_update', 'roadmap'],
  },
  budget_request: {
    label: 'Budget Request',
    description: 'Technology budget and investment proposal',
    defaultSlides: ['title', 'executive_summary', 'investment_request', 'roadmap'],
  },
  strategy_review: {
    label: 'Strategy Review',
    description: 'Technology strategy and vision presentation',
    defaultSlides: ['title', 'executive_summary', 'roadmap', 'risks_opportunities'],
  },
  incident_report: {
    label: 'Incident Report',
    description: 'Major incident post-mortem for executives',
    defaultSlides: ['title', 'executive_summary', 'risks_opportunities'],
  },
};

export const SLIDE_TYPE_CONFIG: Record<SlideType, {
  label: string;
  icon: string;
  description: string;
}> = {
  title: {
    label: 'Title Slide',
    icon: '📋',
    description: 'Opening slide with presentation title and date',
  },
  executive_summary: {
    label: 'Executive Summary',
    icon: '📊',
    description: 'High-level overview of key points',
  },
  kpis: {
    label: 'KPIs & Metrics',
    icon: '📈',
    description: 'Key performance indicators and trends',
  },
  risks_opportunities: {
    label: 'Risks & Opportunities',
    icon: '⚠️',
    description: 'Risk matrix and opportunities assessment',
  },
  investment_request: {
    label: 'Investment Request',
    icon: '💰',
    description: 'Budget and investment proposals',
  },
  roadmap: {
    label: 'Roadmap',
    icon: '🗺️',
    description: 'Technology roadmap and milestones',
  },
  team_update: {
    label: 'Team Update',
    icon: '👥',
    description: 'Team metrics and hiring updates',
  },
  custom: {
    label: 'Custom Slide',
    icon: '✏️',
    description: 'Free-form content slide',
  },
};
